import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { licensesAPI } from '../services/api';
import { format } from 'date-fns';
import './Dashboard.css';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await licensesAPI.dashboard();
      setData(response.data);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchDashboard}>Retry</button>
      </div>
    );
  }

  const { summary, by_type, recent_licenses, expiring_soon_list } = data;

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-description">License management overview</p>
        </div>
        <Link to="/dashboard/licenses/create" className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <path d="M12 4v16m8-8H4" />
          </svg>
          Create License
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon total">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{summary.total}</span>
            <span className="stat-label">Total Licenses</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon active">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{summary.active}</span>
            <span className="stat-label">Active</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon warning">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{summary.expiring_soon}</span>
            <span className="stat-label">Expiring Soon</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon expired">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{summary.expired}</span>
            <span className="stat-label">Expired</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Expiring Soon */}
        <div className="card">
          <div className="card-header">
            <h3>Expiring Soon</h3>
            <span className="badge badge-warning">{expiring_soon_list.length}</span>
          </div>
          <div className="card-content">
            {expiring_soon_list.length === 0 ? (
              <p className="empty-message">No licenses expiring in the next 30 days</p>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Type</th>
                      <th>Expires</th>
                      <th>Days Left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiring_soon_list.map((license) => (
                      <tr key={license.id}>
                        <td>
                          <Link to={`/dashboard/licenses/${license.id}`} className="link">
                            {license.customer_name}
                          </Link>
                        </td>
                        <td className="text-capitalize">{license.license_type}</td>
                        <td>{format(new Date(license.expiry_date), 'MMM d, yyyy')}</td>
                        <td>
                          <span className={`badge ${license.days_until_expiry <= 7 ? 'badge-danger' : 'badge-warning'}`}>
                            {license.days_until_expiry} days
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Recent Licenses */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Licenses</h3>
            <Link to="/dashboard/licenses" className="link">View All</Link>
          </div>
          <div className="card-content">
            {recent_licenses.length === 0 ? (
              <p className="empty-message">No licenses created yet</p>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent_licenses.map((license) => (
                      <tr key={license.id}>
                        <td>
                          <Link to={`/dashboard/licenses/${license.id}`} className="link">
                            {license.customer_name}
                          </Link>
                        </td>
                        <td className="text-capitalize">{license.license_type}</td>
                        <td>
                          <span className={`badge badge-${getStatusColor(license.status, license.is_expired)}`}>
                            {license.is_expired ? 'Expired' : license.status}
                          </span>
                        </td>
                        <td>{format(new Date(license.created_at), 'MMM d, yyyy')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* License Types Distribution */}
        <div className="card">
          <div className="card-header">
            <h3>License Types</h3>
          </div>
          <div className="card-content">
            <div className="type-distribution">
              {by_type.map((item) => (
                <div key={item.license_type} className="type-item">
                  <div className="type-info">
                    <span className="type-name text-capitalize">{item.license_type}</span>
                    <span className="type-count">{item.count}</span>
                  </div>
                  <div className="type-bar">
                    <div
                      className="type-fill"
                      style={{ width: `${(item.count / summary.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getStatusColor = (status, isExpired) => {
  if (isExpired) return 'danger';
  switch (status) {
    case 'active': return 'success';
    case 'pending': return 'warning';
    case 'revoked': return 'danger';
    default: return 'gray';
  }
};

export default Dashboard;
